/**
 * Card de produto em formato lista - Somente visualização
 * Exibe informações de previsão, pedido, estoque e percentual de venda
 */

import React from 'react';
import {
  Card,
  Box,
  Typography,
  Chip,
  LinearProgress,
} from '@mui/material';
import { Inventory } from '@mui/icons-material';
import { Produto } from '@types';
import { FormatUtil } from '@utils';
import { LazyAvatar } from '@components/common';
import theme from '@theme';

interface ProdutoItemListProps {
  produto: Produto;
}

export const ProdutoItemList: React.FC<ProdutoItemListProps> = React.memo(({ produto }) => {
  const getImageUrl = (filename: string): string => {
    if (!filename) return '';
    return FormatUtil.formatImageUrl(`${filename}`);
  };

  // Calcula percentual de venda (pedido / previsão * 100)
  const previsao = produto.previsao || 0;
  const pedido = produto.pedido || 0;
  const percentual = previsao > 0 ? (pedido / previsao) * 100 : 0;
  const corPercentual = percentual >= 50 ? 'success.main' : 'error.main';

  return (
    <Card
      sx={{
        transition: 'box-shadow 0.2s',
        '&:hover': {
          boxShadow: 2,
        },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
        }}
      >
        {/* Coluna 1: Imagem */}
        <LazyAvatar
          sx={{
            width: 60,
            height: 60,
            bgcolor: 'grey.100',
            flexShrink: 0,
            backgroundColor: theme.palette.elite.contrastText,
          }}
          src={getImageUrl(produto.imagem)}
          alt={produto.nome}
          variant="rounded"
          loading="lazy"
        >
          <Inventory sx={{ color: 'grey.400' }} />
        </LazyAvatar>

        {/* Coluna 2: Código e Nome */}
        <Box sx={{ flex: '1 1 auto', minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem', display: 'block' }}>
            Cód: {produto.codigo}
          </Typography>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              fontSize: '0.875rem',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {produto.nome}
          </Typography>
        </Box>

        {/* Coluna 3: Métricas (Previsão, Pedido, Estoque) */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'flex' },
            gap: 0.5,
            flexShrink: 0,
          }}
        >
          <Chip
            size="small"
            label={`Prev: ${previsao}`}
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
          <Chip
            size="small"
            label={`Ped: ${pedido}`}
            color={Number(produto.estoque) > 0 ? 'secondary' : 'success'}
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
          <Chip
            size="small"
            icon={<Inventory sx={{ fontSize: '0.75rem' }} />}
            label={`${produto.estoque}`}
            color={Number(produto.estoque) > 0 ? 'error' : 'success'}
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 22 }}
          />
        </Box>

        {/* Coluna 4: Percentual com barra */}
        <Box
          sx={{
            width: 120,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
              Vendido
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{
                color: corPercentual,
                fontSize: '0.75rem',
              }}
            >
              {percentual.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(percentual, 100)}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'grey.200',
              '& .MuiLinearProgress-bar': {
                backgroundColor: corPercentual,
                borderRadius: 3,
              },
            }}
          />
        </Box>
      </Box>
    </Card>
  );
});

ProdutoItemList.displayName = 'ProdutoItemList';
